import { IsNullOrWhiteSpace } from "@/utils/Utils";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

const dayjsFAMPM = "YYYY-MM-DD hh:mm:ss A";
const dayjsF = "YYYY-MM-DD HH:mm:ss";
const dayjsDate = "YYYY-MM-DD";
// const dayjsMonthDate = "YYYY-MMM-DD";
const dayjsMonthF = "YYYY-MM";
const dayjsMonthFW = "YYYY-MMM";
const yymmdd_hhmm = "YY-M-DD hh:mm"; //

export enum DATE_FORMAT_OPTIONS {
    /**
     *  8:02 PM
     */
    LT = "h:mm A",
    /**
     *  8:02: 18 PM
     */
    LTS = "h: mm: ss A",
    /**
     * 	08/16/2018
     */
    L = "MM/DD/YYYY",
    /**
     * 	08-16-2018
     */
    LM = "DD-MM-YYYY",
    /**
     * 	18/08/2018
     */
    LD = "DD/MM/YYYY",
    /**
     * 	August 16, 2018
     */
    LL = "MMMM D, YYYY",
    /**
     *  August 16, 2018 8:02 PM
     */
    LLL = "MMMM D, YYYY h:mm A",
    /**
     *  Thursday, August 16, 2018 8:02 PM
     */
    LLLL = "dddd, MMMM D, YYYY h:mm A",
    /**
     *  8/16/2018
     */
    l = "M/D/YYYY",
    /**
     *  18/06/2018
     */
    ld = "D/M/YYYY",
    /**
     *  Aug 16, 2018
     */
    ll = "MMM D, YYYY",
    /**
     * 	Aug 16, 2018 8:02 PM
     */
    lll = "MMM D, YYYY h:mm A",
    /**
     * 	Thu, Aug 16, 2018 8:02 PM
     */
    llll = "ddd, MMM D, YYYY h:mm A",
    /**
     * 2024-11-08 13:40:11
     */
    Ll = "YYYY-MM-DD",
    /**
     * 2024-11-08 13:40:11
     */
    LLD = "YYYY-MM-DD h:mm:ss",
}

// LT |	h:mm A  |	8:02 PM
// LTS  |	h:mm:ss | A	8:02:18 PM
// L  |	MM/DD/YYYY  |	08/16/2018
// LL |	MMMM D, YYYY  |	August 16, 2018
// LLL  |	MMMM D, YYYY h:mm A |	August 16, 2018 8:02 PM
// LLLL |	dddd, MMMM D, YYYY h:mm A	 | Thursday, August 16, 2018 8:02 PM
// l  |	M/D/YYYY  |	8/16/2018
// ll |	MMM D, YYYY |	Aug 16, 2018
// lll  |	MMM D, YYYY h:mm A  |	Aug 16, 2018 8:02 PM
// llll |	ddd, MMM D, YYYY h:mm A |	Thu, Aug 16, 2018 8:02 PM

/**
 * E.g. 2024-02-23 22:43:00
 */
export function dayjsFormat(date?: Date) {
    date = date ?? new Date();
    const form = dayjs(date).format(dayjsF);

    return form.toString();
}
/**
 * E.g. 2024-Feb
 */
export function forMonth(date?: Date) {
    date = date ?? new Date();
    const form = dayjs(date).format(dayjsMonthFW);

    return form.toString();
}
/**
 * E.g. 2024-02
 */
export function forMonthNumber(date?: Date) {
    date = date ?? new Date();
    const form = dayjs(date).format(dayjsMonthF);

    return form.toString();
}
/**
 * E.g. 2024-02-02
 */
export function DateOnly(date?: Date | string) {
    if (IsNullOrWhiteSpace(date)) return "--";

    if (typeof date === "string") {
        date = new Date(date);
    }
    const form = dayjs(date).format(dayjsDate);

    return form.toString();
}

export function dayjsFromStr(date?: string): string {
    date = date ?? new Date().toString();
    const form = dayjs(date).format(dayjsF);

    return form.toString();
}

export function fromUTCOffset(date?: string): string {
    date = date ?? new Date().toString();

    let format = "";

    if (dayjs(date).isUTC()) {
        // dayjs.utc("2000-01-01T06:01:02Z").utcOffset(1, true).format();
        format = dayjs.utc(date).utcOffset(1, true).format(dayjsF);
    } else format = dayjs(date).format(dayjsF);

    return format.toString();
}

export function fromUTCOffsetTimeAMP(date?: string): string {
    date = date ?? new Date().toString();

    let format = "";

    if (dayjs(date).isUTC()) {
        // dayjs.utc("2000-01-01T06:01:02Z").utcOffset(1, true).format();
        format = dayjs.utc(date).utcOffset(1, true).format(dayjsFAMPM);
    } else format = dayjs(date).format(dayjsFAMPM);

    return format.toString();
}

export function TimeFromNow(date?: Date | string): string {
    const oneMonthAgo = dayjs().subtract(1, "month");

    if (IsNullOrWhiteSpace(date)) return "--";

    // return dayjs(date).fromNow();
    return dayjs(date).isAfter(oneMonthAgo)
        ? dayjs(date).fromNow()
        : dayjs(date).format(yymmdd_hhmm);
}

// string parsing to date, handling UTC, Date only, Time
export function TryParseDate(
    // {
    //   value,
    //   format,
    // }: {
    value: Date | string,
    format?:
        | "dayjsFAMPM"
        | "dayjsF"
        | "dayjsDate"
        | "dayjsMonthF"
        | "dayjsMonthFW"
        | "yymmdd_hhmm",
    // }
): string | dayjs.Dayjs {
    if (dayjs(value).isUTC()) {
        // dayjs.utc("2000-01-01T06:01:02Z").utcOffset(1, true).format();
        // return dayjs.utc(value).utcOffset(1, true).format(dayjsF);
        return dayjs.utc(value);
    }

    switch (format) {
        case "dayjsFAMPM":
            return dayjs(value).format(dayjsFAMPM);
        case "dayjsF":
            return dayjs(value).format(dayjsF);
        case "dayjsDate":
            return dayjs(value).format(dayjsDate);
        case "dayjsMonthF":
            return dayjs(value).format(dayjsMonthF);
        case "dayjsMonthFW":
            return dayjs(value).format(dayjsMonthFW);
        case "yymmdd_hhmm":
            return dayjs(value).format(yymmdd_hhmm);
        default:
            return dayjs(value);
    }
}

export function formatDate({
    value,
    format = DATE_FORMAT_OPTIONS.LL,
}: {
    value?: Date | string;
    format?: DATE_FORMAT_OPTIONS;
}): string {
    if (IsNullOrWhiteSpace(value)) return "--";

    if (dayjs(value).isUTC()) {
        return dayjs.utc(value).format(format);
    }

    return dayjs(value).format(format);
}

export function TryParse(value: any) {
    if (typeof value === "string" || value instanceof Date) {
        const date = new Date(value);

        if (dayjs(value).isUTC()) {
            return dayjs.utc(value).utcOffset(1, true).format(dayjsFAMPM);
        }

        if (!isNaN(date.getTime())) {
            return dayjs(value).format(dayjsFAMPM);
        }
    }

    if (typeof value === "string") return value?.capitalize();

    return value;
}

// for month check
export function isForMonth(date: string): boolean {
    return dayjs(date).format("YYYY-MMM") === dayjs().format("YYYY-MMM");
}

/**
 *
 * for month check with old date and month only like:
 * @param
 *  2024-Feb, 2024-Mar,
 *
 * i want to check if the date is in the coming month,
 * if not i will disable the edit button
 */
export function isForMonthCheck(date: string): boolean {
    return dayjs(date).isAfter(dayjs().format("YYYY-MMM"));
}

// return date for react big calendar, and date can be UTC, also is string
export function getDateWithUTC(date: Date | string): Date {
    const d = dayjs(date);

    // console.log(
    //     `\n d: ${d}`,
    //     `\n d.utcOffset(): ${d.utcOffset()}`,
    //     `\n d.isUTC(): ${d.isUTC()}`,
    //     `\n date: ${date}`,
    //     `\n d.toDate(): ${d.toDate()}`,
    //     `\n dayjs.utc(date).toDate(): ${dayjs.utc(date).toDate()}`,
    // );

    if (d.utcOffset() !== 0) {
        return dayjs(date).utcOffset(1, true).local().toDate();
    } else if (d.isUTC()) {
        return dayjs.utc(date).local().toDate();
    }

    return dayjs(date).toDate();
}

// get any date from string and return dayjs object also handle UTC and UTC offset
export function getDayjsFromStr(date: string): dayjs.Dayjs {
    const d = dayjs(date);

    if (d.isUTC() || d.utcOffset() !== 0) {
        return dayjs.utc(date).utcOffset(1, true);
    }

    return dayjs(date);
}

// check if string is date and return true or false
// export function isDate(date: string): boolean {
//     return dayjs(date).isValid();
// }
